import { useState, useEffect, useRef, useDeferredValue, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  useWorkshops,
  useCreateWorkshop,
  useAssignWorkshop,
} from "../../../hooks/useWorkshopQueries";

import {
  Badge, PageHeader, Spinner, SearchInput, EmptyState, ErrorState,
  MetricCard, Modal, InfoRow, Avatar, RatingStars,
  formatDate,TopLoader
} from "../../../Component/Workshop/UI";

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_TABS = [
  { key:"ALL",      label:"All"      },
  { key:"ACTIVE",   label:"Active"   },
  { key:"INACTIVE", label:"Inactive" },
];

const SPECIALIZATIONS = [
  "All Types","Car","SUV","Truck","Heavy Vehicle",
  "Hatchback","Luxury","Two-Wheeler",
];

const SORT_OPTIONS = [
  { value:"name",          label:"Name A–Z"          },
  { value:"rating",        label:"Rating (High–Low)"  },
  { value:"activeCases",   label:"Active Cases"       },
  { value:"totalCases",    label:"Total Cases"        },
  { value:"avgTurnaround", label:"Avg Turnaround"     },
];

function useDebounce(value, delay = 600) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const WORKSHOP_DETAIL_TABS = ["Overview", "Active Cases", "Payment History"];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WorkshopsPage() {
  const navigate = useNavigate();
   const location = useLocation(); 
  const [searchParams, setSearchParams] = useSearchParams();
  const globalSearch = searchParams.get("q") || "";
    
    const prefillCaseId = location.state?.prefillCaseId ?? ""; 
    const returnTo      = location.state?.returnTo ?? null;        
    const returnTab     = location.state?.returnTab ?? "Surveys";   
    console.log("prefillCaseId",prefillCaseId);

  // Filters & view
  const [activeTab,    setActiveTab]    = useState("ALL");
  const [search,       setSearch]       = useState(globalSearch);
  const [specFilter,   setSpecFilter]   = useState("All Types");
  const [cityFilter,   setCityFilter]   = useState("");
  const [sortBy,       setSortBy]       = useState("name");
  const [viewMode,     setViewMode]     = useState("grid"); // "grid" | "table"

  const handleSearchChange = (value) => {
    setSearch(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value?.trim()) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true }
    );
  };

  // Modals
  const [detailWs,    setDetailWs]    = useState(null);  // workshop detail drawer
  const [addModal,    setAddModal]    = useState(false);
  const [editModal,   setEditModal]   = useState(null);
  const [assignModal, setAssignModal] = useState(null);  // assign case modal

  // Add/Edit form state
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (globalSearch !== search) setSearch(globalSearch);
  }, [globalSearch]);

  const debouncedSearch = useDebounce(search, 400);
  // const deferredSearch = useDeferredValue(debouncedSearch)

  const queryParams = useMemo(() => ({
  status: activeTab !== "ALL" ? activeTab : undefined,
  search: debouncedSearch || undefined,
  city: cityFilter || undefined,
  specialization: specFilter !== "All Types" ? specFilter : undefined,
}), [activeTab, debouncedSearch, cityFilter, specFilter]);

  const stableDataRef = useRef(null);

  // ── Real query – uncomment when backend is live ─────────────────────────────
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, isFetching } = useWorkshops(queryParams);

    if (data) {
    stableDataRef.current = data;
    }
  const stableData = data || stableDataRef.current;

  const workshops = stableData?.pages.flatMap(p => p.workshops) || [];
  const total = stableData?.pages[0]?.total ?? 0;
  const metrics = stableData?.pages[0]?.metrics || {};
  const hasMore = hasNextPage;
  const loadMore = fetchNextPage;

  const createWorkshop = useCreateWorkshop();
  const assignWorkshop = useAssignWorkshop();

  // ── Handlers ────────────────────────────────────────────────────────────────
  const openAdd = () => { setForm(emptyForm()); setAddModal(true); };
  const openEdit = (ws) => { setForm(wsToForm(ws)); setEditModal(ws); };

  const handleSave = async () => {
    await createWorkshop.mutateAsync(form);
    console.log("Save workshop:", form);
    setAddModal(false); setEditModal(null);
  };

  const handleAssign = async (caseNo) => {
    console.log("Assigning case--", assignModal);
    await assignWorkshop.mutateAsync({ caseId: caseNo, workshopId: assignModal.id, workshopName: assignModal.name });
    console.log("Assign case", caseNo, "→ workshop", assignModal?.id);
    setAssignModal(null);

    if (returnTo) {
    navigate(returnTo, { state: { activeTab: returnTab } });
  }
  };

  // ── Capacity color ───────────────────────────────────────────────────────────
  const capacityColor = (ws) => {
    const pct = ws.currentLoad / ws.capacity;
    if (pct >= 1)   return "var(--wd-red)";
    if (pct >= 0.7) return "var(--wd-orange)";
    return "var(--wd-green)";
  };

  return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* ── Header ── */}
      <PageHeader
        title="Workshops"
        subtitle="Manage repair workshops and track case assignments"
        actions={
          <button className="wd-btn wd-btn-primary" onClick={openAdd}>
            <PlusIcon /> Add Workshop
          </button>
        }
      />

      {/* ── Metric Cards ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12 }}>
        <MetricCard label="Total Workshops"   value={metrics.totalWorkshops ?? 0}        color="#3b82f6" />
        <MetricCard label="Active"            value={metrics.active ?? 0}       color="#22c55e" />
        <MetricCard label="With Active Cases" value={metrics.withActiveCases ?? 0}   color="#a855f7" />
        <MetricCard label="Inactive"          value={metrics.inactive ?? 0} color="#f97316" />
      </div>

      {/* Showing count and clear filters */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div style={{ fontSize:13, color:"var(--wd-t3)" }}>
          Showing <strong style={{ color:"var(--wd-t1)" }}>{workshops.length}</strong>
          {total > workshops.length
            ? <> of <strong style={{ color:"var(--wd-t1)" }}>{total}</strong> workshops</>
            : <> workshop{workshops.length !== 1 ? "s" : ""}</>
          }
          {(search || specFilter !== "All Types" || cityFilter) && (
            <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ marginLeft:8, fontSize:11 }}
              onClick={() => { setSearch(""); setSpecFilter("All Types"); setCityFilter(""); }}>
              ✕ Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        {/* Status tabs */}
        <div style={{ display:"flex", gap:8 }}>
          {STATUS_TABS.map(t => (
            <button key={t.key}
              className={`wd-filter-tab ${activeTab === t.key ? "active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >{t.label}</button>
          ))}
        </div>

        {/* Controls */}
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <SearchInput value={search} onChange={handleSearchChange} placeholder="Name, owner, city, ID..." />

          <select className="wd-input wd-select" style={{ width:"auto", minWidth:140 }}
            value={specFilter} onChange={e => setSpecFilter(e.target.value)}>
            {SPECIALIZATIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <input className="wd-input" style={{ width:110 }}
            placeholder="City..."
            value={cityFilter} onChange={e => setCityFilter(e.target.value)} />

          <select className="wd-input wd-select" style={{ width:"auto", minWidth:160 }}
            value={sortBy} onChange={e => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {/* View toggle */}
          <div style={{ display:"flex", gap:2 }}>
            <button className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
              style={viewMode === "grid" ? { background:"var(--wd-hover)", color:"var(--wd-t1)" } : {}}
              onClick={() => setViewMode("grid")}><GridIcon /></button>
            <button className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
              style={viewMode === "table" ? { background:"var(--wd-hover)", color:"var(--wd-t1)" } : {}}
              onClick={() => setViewMode("table")}><ListIcon /></button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {isLoading ? (<Spinner />) :
       isError   ? ( <ErrorState message="Failed to load workshops" onRetry={() => {}} /> ):
       workshops.length === 0 ? ( <EmptyState message="No workshops match your filters" /> ):
       
       (
        <>{isFetching && <TopLoader/>}
        {viewMode === "grid"
         ? <GridView    workshops={workshops} onDetail={setDetailWs} onEdit={openEdit} onAssign={setAssignModal} capacityColor={capacityColor} />
         : <TableView   workshops={workshops} onDetail={setDetailWs} onEdit={openEdit} onAssign={setAssignModal} capacityColor={capacityColor} />}
  </>
        )
      }

      {hasMore && !isFetchingNextPage && (
        <div style={{ display:"flex", justifyContent:"center" }}>
          <button
            className="wd-btn wd-btn-ghost"
            onClick={loadMore}
            style={{ minWidth:220, justifyContent:"center" }}
          >
            Load more workshops
          </button>
        </div>
      )}

      {/* ── Workshop Detail Modal ── */}
      <WorkshopDetailModal
        ws={detailWs}
        onClose={() => setDetailWs(null)}
        onEdit={() => { openEdit(detailWs); setDetailWs(null); }}
        onAssign={() => { setAssignModal(detailWs); setDetailWs(null); }}
        onNavigate={navigate}
        capacityColor={capacityColor}
      />

      {/* ── Add / Edit Modal ── */}
      <WorkshopFormModal
        open={addModal || !!editModal}
        isEdit={!!editModal}
        form={form}
        setForm={setForm}
        onClose={() => { setAddModal(false); setEditModal(null); }}
        onSave={handleSave}
      />

      {/* ── Assign Case Modal ── */}
      <AssignCaseModal
        ws={assignModal}
        onClose={() => setAssignModal(null)}
        onAssign={handleAssign}
        prefillCaseId={prefillCaseId}
      />
    </div>
  );
}

// ─── Grid View ────────────────────────────────────────────────────────────────
function GridView({ workshops, onDetail, onEdit, onAssign, capacityColor }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(310px, 1fr))", gap:16 }}>
      {workshops.map(ws => (
        <WorkshopCard key={ws.id} ws={ws} onDetail={onDetail} onEdit={onEdit} onAssign={onAssign} capacityColor={capacityColor} />
      ))}
    </div>
  );
}

// ─── Workshop Card ────────────────────────────────────────────────────────────
function WorkshopCard({ ws, onDetail, onEdit, onAssign, capacityColor }) {
  const loadPct = Math.min((ws.currentLoad / ws.capacity) * 100, 100);
  const color   = capacityColor(ws);

  return (
    <div className="wd-card"
      style={{ padding:20, cursor:"pointer", transition:"border-color .15s" }}
      onClick={() => onDetail(ws)}
      onMouseEnter={e => e.currentTarget.style.borderColor = "var(--wd-blue)"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--wd-border)"}
    >
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          <Avatar name={ws.name} size={46} />
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:"var(--wd-t1)", lineHeight:1.3 }}>{ws.name}</div>
            <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:2 }}>📍 {ws.city}, {ws.state}</div>
            <RatingStars rating={ws.rating} />
          </div>
        </div>
        <Badge variant={ws.status === "ACTIVE" ? "green" : "gray"}>{ws.status}</Badge>
      </div>

      {/* Capacity bar */}
      <div style={{ marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
          <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>Capacity</span>
          <span style={{ fontSize:11, color, fontWeight:700 }}>{ws.currentLoad}/{ws.capacity} active</span>
        </div>
        <div style={{ height:5, borderRadius:3, background:"var(--wd-hover)" }}>
          <div style={{ height:"100%", borderRadius:3, width:`${loadPct}%`, background:color, transition:"width .3s" }} />
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:14 }}>
        <MiniStat label="Active"   value={ws.activeCases}   color={ws.activeCases > 0 ? "var(--wd-orange)" : "var(--wd-t1)"} />
        <MiniStat label="Total"    value={ws.totalCases}    />
        <MiniStat label="Avg Days" value={`${ws.avgTurnaround}d`} />
      </div>

      {/* Specialization tags */}
      <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:14 }}>
        {ws.specialization.map(s => (
          <span key={s} className="wd-badge wd-badge-blue" style={{ fontSize:10 }}>{s}</span>
        ))}
      </div>

      {/* Owner */}
      <div style={{ fontSize:12, color:"var(--wd-t3)", marginBottom:12 }}>
        👤 {ws.ownerName} · 📞 {ws.phone}
      </div>

      <div className="wd-divider" style={{ margin:"0 0 12px" }} />

      {/* Actions */}
      <div style={{ display:"flex", gap:6 }} onClick={e => e.stopPropagation()}>
        <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ flex:1, justifyContent:"center" }}
          onClick={() => onDetail(ws)}>View Details</button>
        <button className="wd-btn wd-btn-primary wd-btn-sm"
          onClick={() => onAssign(ws)}>Assign Case</button>
        <button className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
          onClick={() => onEdit(ws)} title="Edit"><EditIcon /></button>
      </div>
    </div>
  );
}

// ─── Table View ───────────────────────────────────────────────────────────────
function TableView({ workshops, onDetail, onEdit, onAssign, capacityColor }) {
  return (
    <div className="wd-card">
      <div className="wd-table-wrap">
        <table className="wd-table">
          <thead>
            <tr>
              <th>WORKSHOP</th>
              <th>LOCATION</th>
              <th>OWNER</th>
              <th>CAPACITY</th>
              <th>ACTIVE CASES</th>
              <th>TOTAL CASES</th>
              <th>AVG DAYS</th>
              <th>RATING</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {workshops.map(ws => {
              const loadPct = Math.min((ws.currentLoad / ws.capacity) * 100, 100);
              const color   = capacityColor(ws);
              return (
                <tr key={ws.id} onClick={() => onDetail(ws)}>
                  {/* Workshop name */}
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <Avatar name={ws.name} size={32} />
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{ws.name}</div>
                        <div style={{ fontSize:11, color:"var(--wd-t3)", fontFamily:"var(--wd-mono)" }}>{ws.id}</div>
                      </div>
                    </div>
                  </td>
                  {/* Location */}
                  <td>
                    <div style={{ fontSize:13 }}>{ws.city}</div>
                    <div style={{ fontSize:11, color:"var(--wd-t3)" }}>{ws.state}</div>
                  </td>
                  {/* Owner */}
                  <td style={{ color:"var(--wd-t2)", fontSize:13 }}>{ws.ownerName}</td>
                  {/* Capacity bar */}
                  <td style={{ minWidth:120 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ flex:1, height:4, borderRadius:2, background:"var(--wd-hover)" }}>
                        <div style={{ height:"100%", borderRadius:2, width:`${loadPct}%`, background:color }} />
                      </div>
                      <span style={{ fontSize:11, color, fontWeight:700, whiteSpace:"nowrap" }}>{ws.currentLoad}/{ws.capacity}</span>
                    </div>
                  </td>
                  {/* Active cases */}
                  <td>
                    <span style={{ fontWeight:700, fontSize:16, color: ws.activeCases > 0 ? "var(--wd-orange)" : "var(--wd-t3)" }}>
                      {ws.activeCases}
                    </span>
                  </td>
                  {/* Total cases */}
                  <td style={{ color:"var(--wd-t2)" }}>{ws.totalCases}</td>
                  {/* Avg turnaround */}
                  <td style={{ color:"var(--wd-t2)" }}>{ws.avgTurnaround} days</td>
                  {/* Rating */}
                  <td><RatingStars rating={ws.rating} /></td>
                  {/* Status */}
                  <td><Badge variant={ws.status === "ACTIVE" ? "green" : "gray"}>{ws.status}</Badge></td>
                  {/* Actions */}
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display:"flex", gap:5 }}>
                      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => onDetail(ws)}>View</button>
                      <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => onAssign(ws)}>Assign</button>
                      <button className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm" onClick={() => onEdit(ws)}><EditIcon /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Workshop Detail Modal ────────────────────────────────────────────────────
function WorkshopDetailModal({ ws, onClose, onEdit, onAssign, onNavigate, capacityColor }) {
  const [tab, setTab] = useState("Overview");

  if (!ws) return null;

  const loadPct = Math.min((ws.currentLoad / ws.capacity) * 100, 100);
  const color   = capacityColor(ws);

  return (
    <Modal open={!!ws} onClose={onClose} title="Workshop Details" maxWidth={680}
      footer={
        <div style={{ display:"flex", justifyContent:"space-between", width:"100%", alignItems:"center" }}>
          <button className="wd-btn wd-btn-ghost" onClick={onClose}>Close</button>
          <div style={{ display:"flex", gap:8 }}>
            <button className="wd-btn wd-btn-ghost" onClick={onEdit}><EditIcon /> Edit</button>
            <button className="wd-btn wd-btn-primary" onClick={onAssign}>Assign Case</button>
          </div>
        </div>
      }
    >
      {/* Workshop identity */}
      <div style={{ display:"flex", alignItems:"center", gap:14, padding:"4px 0 4px" }}>
        <Avatar name={ws.name} size={52} />
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <span style={{ fontWeight:700, fontSize:17, color:"var(--wd-t1)" }}>{ws.name}</span>
            <Badge variant={ws.status === "ACTIVE" ? "green" : "gray"}>{ws.status}</Badge>
          </div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:3 }}>
            {ws.id} · 📍 {ws.address}, {ws.city} – {ws.pincode}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:5 }}>
            <RatingStars rating={ws.rating} />
            <span style={{ fontSize:11, color:"var(--wd-t3)" }}>·</span>
            <span style={{ fontSize:12, color:"var(--wd-t3)" }}>Joined {formatDate(ws.joinedAt)}</span>
          </div>
        </div>
      </div>

      {/* Capacity bar */}
      <div style={{ background:"var(--wd-hover)", borderRadius:10, padding:"12px 14px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
          <span style={{ fontSize:12, color:"var(--wd-t2)", fontWeight:600 }}>Workshop Capacity</span>
          <span style={{ fontSize:12, color, fontWeight:700 }}>{ws.currentLoad} of {ws.capacity} slots used</span>
        </div>
        <div style={{ height:6, borderRadius:3, background:"var(--wd-border-md)" }}>
          <div style={{ height:"100%", borderRadius:3, width:`${loadPct}%`, background:color, transition:"width .4s" }} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid var(--wd-border)", margin:"4px 0" }}>
        {WORKSHOP_DETAIL_TABS.map(t => (
          <button key={t} className={`wd-detail-tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "Overview" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <InfoRow label="Owner Name"    value={ws.name} />
          <InfoRow label="Phone"         value={ws.phone} mono />
          <InfoRow label="Email"         value={ws.email} />
          <InfoRow label="GST No"        value={ws.gstNo} mono />
          <InfoRow label="City"          value={`${ws.city}`} />
          <InfoRow label="Pincode"       value={ws.pincode} mono />
          <InfoRow label="Active Cases"  value={ws.activeCases} />
          <InfoRow label="Total Cases"   value={ws.totalCases} />
          <InfoRow label="Avg Turnaround"value={`${ws.avgTurnaround} days`} />
          <InfoRow label="Last Case"     value={formatDate(ws.lastCaseAt)} />
          <div style={{ gridColumn:"1 / -1" }}>
            <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>Specialization</span>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
              {ws.specialization.map(s => <span key={s} className="wd-badge wd-badge-blue">{s}</span>)}
            </div>
          </div>
        </div>
      )}

      {tab === "Active Cases" && (
        ws.activeCasesList.length === 0
          ? <EmptyState message="No active cases assigned" />
          : <div className="wd-card" style={{ overflow:"hidden" }}>
              <div className="wd-table-wrap">
                <table className="wd-table">
                  <thead>
                    <tr><th>CASE NO</th><th>CUSTOMER</th><th>VEHICLE</th><th>STATUS</th><th>SINCE</th><th></th></tr>
                  </thead>
                  <tbody>
                    {ws.activeCasesList.map(c => (
                      <tr key={c.caseNo} onClick={() => { onClose(); onNavigate(`/admin/cases/${c.caseNo}`); }}>
                        <td style={{ fontFamily:"var(--wd-mono)", fontSize:12, color:"var(--wd-blue)", fontWeight:600 }}>{c.caseNo}</td>
                        <td style={{ fontSize:13 }}>{c.customer}</td>
                        <td style={{ fontFamily:"var(--wd-mono)", fontSize:12 }}>{c.vehicle}</td>
                        <td><span className="wd-badge wd-badge-orange" style={{ fontSize:10 }}>{c.status.replace(/_/g," ")}</span></td>
                        <td style={{ fontSize:12, color:"var(--wd-t3)" }}>{formatDate(c.since)}</td>
                        <td><ArrowIcon /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
      )}

      {tab === "Payment History" && (
        <div className="wd-card" style={{ overflow:"hidden" }}>
          <div className="wd-table-wrap">
            <table className="wd-table">
              <thead>
                <tr><th>MONTH</th><th>CASES</th><th>AMOUNT</th><th>STATUS</th></tr>
              </thead>
              <tbody>
                {ws.paymentHistory.map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight:500 }}>{p.month}</td>
                    <td style={{ color:"var(--wd-t2)" }}>{p.cases}</td>
                    <td style={{ fontFamily:"var(--wd-mono)", fontWeight:600, color:"var(--wd-t1)" }}>
                      {p.amount > 0 ? `₹${p.amount.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td>
                      <Badge variant={{ PAID:"green", PENDING:"yellow", "N/A":"gray" }[p.status] || "gray"}>
                        {p.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Workshop Form Modal (Add / Edit) ─────────────────────────────────────────
function WorkshopFormModal({ open, isEdit, form, setForm, onClose, onSave }) {
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const toggleSpec = (spec) => setForm(f => ({
    ...f,
    specialization: f.specialization.includes(spec)
      ? f.specialization.filter(s => s !== spec)
      : [...f.specialization, spec],
  }));

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Workshop" : "Add New Workshop"} maxWidth={600}
      footer={<>
        <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="wd-btn wd-btn-primary" onClick={onSave}>
          {isEdit ? "Save Changes" : "Add Workshop"}
        </button>
      </>}
    >
      {/* Basic Info */}
      <SectionHeading>Basic Information</SectionHeading>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <FormField label="Workshop Name *">
          <input className="wd-input" placeholder="e.g. ABC Motors" value={form.name} onChange={set("name")} />
        </FormField>
        <FormField label="Owner Name *">
          <input className="wd-input" placeholder="Owner's full name" value={form.ownerName} onChange={set("ownerName")} />
        </FormField>
        <FormField label="Phone *">
          <input className="wd-input" placeholder="Phone number" value={form.phone} 
          onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
          setForm(f => ({ ...f, phone: digits }));
        }}
        maxLength={10}
        inputMode="numeric" />
        </FormField>
        <FormField label="Email">
          <input className="wd-input" type="email" placeholder="Email address" value={form.email} onChange={set("email")} />
        </FormField>
        <FormField label="GST Number">
          <input className="wd-input" placeholder="GST No" value={form.gstNo} onChange={set("gstNo")} />
        </FormField>
        <FormField label="Capacity (max vehicles)">
          <input className="wd-input" type="number" min={1} placeholder="e.g. 6" value={form.capacity} onChange={set("capacity")} />
        </FormField>
      </div>

      {/* Address */}
      <SectionHeading>Address</SectionHeading>
      <FormField label="Street Address *">
        <input className="wd-input" placeholder="Full address" value={form.address} onChange={set("address")} />
      </FormField>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
        <FormField label="City *">
          <input className="wd-input" placeholder="City" value={form.city} onChange={set("city")} />
        </FormField>
        <FormField label="State *">
          <input className="wd-input" placeholder="State" value={form.state} onChange={set("state")} />
        </FormField>
        <FormField label="Pincode *">
          <input className="wd-input" placeholder="Pincode" value={form.pincode} onChange={set("pincode")} />
        </FormField>
      </div>

      {/* Specialization */}
      <SectionHeading>Specialization</SectionHeading>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {["Car","SUV","Truck","Heavy Vehicle","Hatchback","Luxury","Two-Wheeler"].map(spec => {
          const active = form.specialization.includes(spec);
          return (
            <button key={spec}
              className={`wd-btn wd-btn-ghost wd-btn-sm`}
              style={active ? { borderColor:"var(--wd-blue)", color:"var(--wd-blue)", background:"var(--wd-blue-soft)" } : {}}
              onClick={() => toggleSpec(spec)}
            >
              {active && "✓ "}{spec}
            </button>
          );
        })}
      </div>

      {/* Status */}
      <SectionHeading>Status</SectionHeading>
      <div style={{ display:"flex", gap:8 }}>
        {["ACTIVE","INACTIVE"].map(s => (
          <button key={s}
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={form.status === s ? { borderColor:"var(--wd-blue)", color:"var(--wd-blue)", background:"var(--wd-blue-soft)" } : {}}
            onClick={() => setForm(f => ({ ...f, status: s }))}
          >
            {s}
          </button>
        ))}
      </div>
    </Modal>
  );
}

// ─── Assign Case Modal ────────────────────────────────────────────────────────
function AssignCaseModal({ ws, onClose, onAssign, prefillCaseId }) {
  const [caseNo, setCaseNo] = useState(prefillCaseId || "");

   useEffect(() => {
    if (ws && prefillCaseId) {
      setCaseNo(prefillCaseId);
    }
  }, [ws, prefillCaseId]);   // ← runs when modal opens (ws changes) or prefill changes

  if (!ws) return null;
  // ... rest unchanged


  return (
    <Modal open={!!ws} onClose={onClose} title={`Assign Case → ${ws.name}`}
      footer={<>
        <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="wd-btn wd-btn-primary" onClick={() => onAssign(caseNo)} disabled={!caseNo.trim()}>
          Assign Workshop
        </button>
      </>}
    >
      {/* Capacity warning */}
      {ws.capacity > 0 && ws.currentLoad >= ws.capacity && (
        <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(239,68,68,.08)", borderRadius:10, border:"1px solid rgba(239,68,68,.15)" }}>
          <span style={{ fontSize:16 }}>⚠️</span>
          <span style={{ fontSize:13, color:"var(--wd-red)" }}>
            This workshop is at full capacity ({ws.currentLoad}/{ws.capacity}). Assigning may cause delays.
          </span>
        </div>
      )}

      {/* Workshop summary */}
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:"var(--wd-hover)", borderRadius:10 }}>
        <Avatar name={ws.name} size={40} />
        <div>
          <div style={{ fontWeight:600, fontSize:13 }}>{ws.name}</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)" }}>📍 {ws.city} · 🔧 {ws.specialization.join(", ")}</div>
          <div style={{ display:"flex", gap:8, marginTop:4 }}>
            <RatingStars rating={ws.rating} />
            <span style={{ fontSize:12, color:"var(--wd-t3)" }}>· {ws.avgTurnaround} days avg</span>
          </div>
        </div>
      </div>

      <FormField label="Case Number *">
        <input className="wd-input" placeholder="e.g. CPA1023"
          value={caseNo} onChange={e => setCaseNo(e.target.value)} />
      </FormField>

      <div style={{ padding:"10px 14px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)" }}>
        <span style={{ fontSize:12, color:"var(--wd-blue)" }}>
          ℹ️ The case status will automatically update to <strong>WORKSHOP_ASSIGNED</strong> and the customer will be notified.
        </span>
      </div>
    </Modal>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function MiniStat({ label, value, color = "var(--wd-t1)" }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
      <span style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", fontWeight:600, letterSpacing:".05em" }}>{label}</span>
      <span style={{ fontSize:16, fontWeight:700, color }}>{value}</span>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".06em", paddingBottom:2, borderBottom:"1px solid var(--wd-border)", marginBottom:4 }}>
      {children}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      <label className="wd-form-label">{label}</label>
      {children}
    </div>
  );
}

function emptyForm() {
  return { name:"", ownerName:"", phone:"", email:"", gstNo:"", address:"", city:"", state:"", pincode:"", capacity:"", specialization:[], status:"ACTIVE" };
}

function wsToForm(ws) {
  return { name:ws.name, ownerName:ws.ownerName, phone:ws.phone, email:ws.email, gstNo:ws.gstNo, address:ws.address, city:ws.city, state:ws.state, pincode:ws.pincode, capacity:String(ws.capacity), specialization:[...ws.specialization], status:ws.status };
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function PlusIcon()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function EditIcon()   { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function GridIcon()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function ListIcon()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function ArrowIcon()  { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>; }